"use client"

import * as React from "react"
import { create } from "zustand"

import { X } from "lucide-react"

import { Button } from "@/components/ui/button"
import { WheelFloor } from "@/components/wheel/wheel-floor"

/* The wheel opened from a reward, over whatever screen the reader is on:
   the same floor as /wheel, without the rail and without a solid ground.
   The screen stays visible under a dark tint, and the spin's own vignette
   then deepens over it. The Monthly Run flips the store; the creator header
   mounts the host, so the menu that opened it can close underneath without
   taking the wheel with it. */

interface TakeoverState {
  open: boolean
  show: () => void
  hide: () => void
}

export const useWheelTakeover = create<TakeoverState>()((set) => ({
  open: false,
  show: () => set({ open: true }),
  hide: () => set({ open: false }),
}))

export const selectTakeoverOpen = (s: TakeoverState) => s.open
export const selectShowTakeover = (s: TakeoverState) => s.show
export const selectHideTakeover = (s: TakeoverState) => s.hide

const OUT_MS = 520

export function WheelTakeover() {
  const open = useWheelTakeover(selectTakeoverOpen)
  const hide = useWheelTakeover(selectHideTakeover)
  // Mounted a beat past "open" so the cover can fade out before it goes.
  const [mounted, setMounted] = React.useState(open)
  React.useEffect(() => {
    if (open) {
      setMounted(true)
      return
    }
    const t = window.setTimeout(() => setMounted(false), OUT_MS)
    return () => window.clearTimeout(t)
  }, [open])
  React.useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") hide()
    }
    document.addEventListener("keydown", onKey)
    return () => document.removeEventListener("keydown", onKey)
  }, [open, hide])
  if (!mounted) return null
  return (
    <div
      className={
        "fixed inset-0 z-[300] overflow-y-auto bg-black/75 " +
        (open ? "wheel-takeover-in" : "wheel-takeover-out")
      }
    >
      {/* The room comes up first, then the wheel settles into it. */}
      <div className={open ? "wheel-takeover-floor-in" : ""}>
        <WheelFloor rail={false} solid={false} />
      </div>
      <Button
        type="button"
        variant="ghost"
        size="icon-xs"
        aria-label="Close"
        onClick={hide}
        className="absolute right-4 top-4 z-[1] text-grey-400 hover:text-white"
      >
        <X />
      </Button>
    </div>
  )
}
